import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect, useRef } from "react";
import { Shield, Calendar, CheckSquare, Square, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Link } from "wouter";

const CHECKLIST_KEY = "physio_hcpc_checklist";

const CHECKLIST_ITEMS = [
  { id: "profile", label: "I have maintained a CPD profile", standard: "Standard 1" },
  { id: "range", label: "I have undertaken a range of CPD activities", standard: "Standard 2" },
  { id: "benefit", label: "My CPD activities have benefited patients / service users", standard: "Standard 3" },
  { id: "summary", label: "I have a summary of my CPD and can explain its relevance to practice", standard: "Standard 4" },
];

const SOP_SECTIONS = [
  {
    title: "Professional autonomy and accountability",
    content: "Physiotherapists must practise within their scope of competence, maintain fitness to practise, and accept personal responsibility for their professional conduct and actions.",
  },
  {
    title: "Assessment, intervention and evaluation",
    content: "Must gather appropriate information, formulate clinical reasoning, plan and deliver evidence-based physiotherapy, and evaluate outcomes systematically.",
  },
  {
    title: "Communication, partnership and teamworking",
    content: "Must communicate effectively with patients, carers, and the wider multi-disciplinary team; involve patients in shared decision-making.",
  },
  {
    title: "Equality, diversity and inclusion",
    content: "Must promote equality, value diversity, and ensure services are accessible, adapting approaches to meet individual needs without discrimination.",
  },
  {
    title: "Research and service development",
    content: "Must apply evidence-based practice, engage with research, participate in audit and quality improvement, and contribute to service development.",
  },
];

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const today = new Date();
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function PhysioHcpcRevalidation() {
  const [renewalDate, setRenewalDate] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(CHECKLIST_KEY) ?? "{}"); }
    catch { return {}; }
  });
  const [openSop, setOpenSop] = useState<number | null>(null);
  const [cpdCount, setCpdCount] = useState<number | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const res = await fetch("/api/physio/settings");
        if (res.ok) {
          const data = await res.json();
          if (data.hcpcRenewalDate) setRenewalDate(data.hcpcRenewalDate);
        }
      } finally {
        setSettingsLoading(false);
      }
    };
    loadSettings();

    fetch("/api/physio/cpd/summary")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCpdCount(d.count); });
  }, []);

  useEffect(() => {
    localStorage.setItem(CHECKLIST_KEY, JSON.stringify(checklist));
  }, [checklist]);

  const handleRenewalDateChange = (value: string) => {
    setRenewalDate(value);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSettingsSaving(true);
      try {
        await fetch("/api/physio/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hcpcRenewalDate: value || null }),
        });
      } finally {
        setSettingsSaving(false);
      }
    }, 800);
  };

  const toggleChecklist = (id: string) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const completedCount = CHECKLIST_ITEMS.filter(i => checklist[i.id]).length;
  const days = renewalDate ? daysUntil(renewalDate) : null;
  const urgency = days !== null ? (days < 30 ? "red" : days < 90 ? "amber" : "green") : "slate";

  const urgencyColors = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };

  return (
    <PhysioLayout title="HCPC Revalidation Dashboard">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">HCPC Revalidation Dashboard</h2>
              <p className="text-emerald-300 text-sm">2-year renewal cycle · Health & Care Professions Council</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            Track your HCPC registration renewal, CPD audit readiness, and Standards of Proficiency for Physiotherapists. HCPC randomly audits approximately 2.5% of registrants at each renewal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`rounded-2xl border p-5 ${urgencyColors[urgency]}`}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5" />
              <span className="text-sm font-semibold">Registration Renewal</span>
              {settingsSaving && <Loader2 className="h-3.5 w-3.5 animate-spin ml-auto opacity-60" />}
            </div>
            {settingsLoading ? (
              <div className="h-9 bg-white/30 rounded-lg animate-pulse mt-2" />
            ) : (
              <input
                type="date"
                value={renewalDate}
                onChange={(e) => handleRenewalDateChange(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-current/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500 mt-2"
                placeholder="Set renewal date"
              />
            )}
            {days !== null && (
              <p className="text-sm font-bold mt-2">
                {days > 0 ? `${days} days remaining` : days === 0 ? "Renewal due today" : `${Math.abs(days)} days overdue`}
              </p>
            )}
            {!renewalDate && !settingsLoading && (
              <p className="text-xs mt-2 opacity-70">Enter your HCPC renewal date — saved to your account</p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-900">Audit Checklist</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{completedCount}/{CHECKLIST_ITEMS.length}</p>
            <p className="text-xs text-slate-500">HCPC standards met</p>
            <div className="mt-2 h-1.5 bg-slate-100 rounded-full">
              <div
                className="h-1.5 bg-emerald-500 rounded-full transition-all"
                style={{ width: `${(completedCount / CHECKLIST_ITEMS.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-900">CPD Activities</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {cpdCount === null ? "—" : cpdCount}
            </p>
            <p className="text-xs text-slate-500">Logged this year</p>
            <Link href="/physio/cpd">
              <button className="mt-2 text-xs text-emerald-700 hover:text-emerald-800 font-medium">
                View CPD Log →
              </button>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">HCPC CPD Audit Readiness Checklist</h3>
          <p className="text-sm text-slate-500 mb-4 leading-relaxed">
            The HCPC requires all four CPD standards to be met. Tick each standard as you can evidence it in your CPD profile.
          </p>
          <div className="space-y-3">
            {CHECKLIST_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => toggleChecklist(item.id)}
                className="flex items-start gap-3 w-full text-left p-3 rounded-xl hover:bg-slate-50 transition-colors"
              >
                {checklist[item.id]
                  ? <CheckSquare className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  : <Square className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />
                }
                <div>
                  <span className="text-xs font-semibold text-emerald-700 mr-2">{item.standard}</span>
                  <span className="text-sm text-slate-700">{item.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">HCPC Standards of Proficiency — Physiotherapists</h3>
          <p className="text-sm text-slate-500 mb-4">Key thematic areas from the HCPC Standards of Proficiency (2023). Click to expand each section.</p>
          <div className="space-y-2">
            {SOP_SECTIONS.map((section, i) => (
              <div key={i} className="border border-slate-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenSop(openSop === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                  {openSop === i ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {openSop === i && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-slate-600 leading-relaxed">{section.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">
            Reference: HCPC Standards of Proficiency — Physiotherapists (2023). For full standards visit hcpc-uk.org.
          </p>
        </div>
      </div>
    </PhysioLayout>
  );
}
